/**
 * Essay Schema
 * A content type that combines lesson content (Page) with essay writing questions
 */

import type { ContentConfig, LayoutConfig, ScoringConfig } from '@ai-content-hub/content-core';
import type { PageElementData } from '@ai-content-hub/content-elements';
import type { TiptapJSON } from '@ai-content-hub/content-kit';

/**
 * Split ratio options for the layout
 */
export type SplitRatio = '50-50' | '60-40' | '40-60' | '70-30' | '30-70';

/**
 * Grading type option (fetched from API)
 */
export interface EssayGradingType {
    id: string;
    code: string;
    name: string;
}

/**
 * Hardcoded grading question types for AI scoring
 * Matches VALID_QUESTION_TYPES in EssayPlayer
 */
export const GRADING_QUESTION_TYPES = [
    { code: 'QT_LISTEN_SUMMARIZE' as const, name: 'Summarize Spoken Text' },
    { code: 'QT_WRITE_SUMMARIZE_WRITTEN_TEXT' as const, name: 'Summarize Written Text' },
    { code: 'QT_WRITE_ESSAY' as const, name: 'Write Essay' },
] as const;

/**
 * Essay question definition
 */
export interface EssayQuestion {
    /** Question prompt (plain text or rich-text JSON) */
    question: string | TiptapJSON;
    /** Minimum word count */
    minWords?: number;
    /** Maximum word count */
    maxWords?: number;
    /** Whether auto-grading is enabled */
    autoGrading?: boolean;
    /** Selected grading type ID (from API) */
    gradingTypeId?: string;
    /** Grading question type code for AI scoring: QT_LISTEN_SUMMARIZE | QT_WRITE_SUMMARIZE_WRITTEN_TEXT | QT_WRITE_ESSAY */
    gradingQuestionType?: string;
    /** Learner-facing rationale displayed after submission/review. */
    explanation?: string;
}

/**
 * Layout configuration for Essay
 */
export interface EssayLayoutConfig extends LayoutConfig {
    /** Split ratio between left (content) and right (writing area) panels */
    splitRatio?: SplitRatio;
    /** Whether to stack panels vertically on mobile */
    responsive?: boolean;
    /** Minimum height for panels */
    minHeight?: string;
}

/**
 * Scoring configuration for Essay
 */
export interface EssayScoringConfig extends ScoringConfig {
    /** Passing threshold (0-1) */
    passingThreshold?: number;
}

/**
 * Essay Config
 */
export interface EssayConfig extends Omit<ContentConfig, 'layout' | 'scoring'> {
    layout?: EssayLayoutConfig;
    scoring?: EssayScoringConfig;
}

/**
 * Essay Data
 */
export interface EssayData {
    /** Optional title */
    title?: string;
    /** Optional description / instructions */
    description?: string;
    /** Lesson content using Page element */
    lessonContent: PageElementData;
    /** Essay question definition */
    essayQuestion: EssayQuestion;
}

/**
 * Essay Content - complete content item
 */
export interface EssayContent {
    id: string;
    type: 'essay';
    version: string;
    data: EssayData;
    config?: EssayConfig;
}

/**
 * Default i18n translations
 */
export const defaultEssayI18n = {
    en: {
        contentTab: 'Content',
        questionsTab: 'Questions',
        submit: 'Submit',
        wordCount: 'Word count',
        minWords: 'Min',
        maxWords: 'Max',
        grading: 'Grading your essay…',
        tryAgain: 'Try again',
        essayInstructionsOptional: 'Instructions (optional)',
        essayEnterTitle: 'Enter title',
        essayEnterInstructions: 'Enter instructions for the exercise',
        essayQuestionPlaceholder: 'Write an essay discussing the advantages and disadvantages of…',
        essayMinWords: 'Minimum words',
        essayMaxWords: 'Maximum words',
        essayAutoGradingLabel: 'Auto-grading',
        essayGradingType: 'Grading type',
        essaySelectGradingType: '— Select grading type —',
        essayPlaceholder: 'Write your essay here...',
        wordLabel: 'word',
        wordsLabel: 'words',
        submittedSuccessfully: 'Your essay has been submitted successfully.',
        explanation: 'Explanation',
        essayTab: 'Essay',
        essayQuestionRequired: 'Essay question is required.',
        essayMaxWordsBelowMin: 'Maximum words must be greater than or equal to minimum words.',
    },
    vi: {
        contentTab: 'Nội dung',
        questionsTab: 'Câu hỏi',
        submit: 'Nộp bài',
        wordCount: 'Số từ',
        minWords: 'Tối thiểu',
        maxWords: 'Tối đa',
        grading: 'Đang chấm bài…',
        tryAgain: 'Thử lại',
        essayInstructionsOptional: 'Hướng dẫn (tuỳ chọn)',
        essayEnterTitle: 'Nhập tiêu đề',
        essayEnterInstructions: 'Nhập hướng dẫn cho bài tập',
        essayQuestionPlaceholder: 'Viết bài luận thảo luận ưu và nhược điểm của…',
        essayMinWords: 'Số từ tối thiểu',
        essayMaxWords: 'Số từ tối đa',
        essayAutoGradingLabel: 'Tự động chấm điểm',
        essayGradingType: 'Loại chấm điểm',
        essaySelectGradingType: '— Chọn loại chấm điểm —',
        essayPlaceholder: 'Viết bài luận của bạn tại đây...',
        wordLabel: 'từ',
        wordsLabel: 'từ',
        submittedSuccessfully: 'Bài luận của bạn đã được nộp thành công.',
        explanation: 'Giải thích',
        essayTab: 'Bài luận',
        essayQuestionRequired: 'Vui lòng nhập câu hỏi bài luận.',
        essayMaxWordsBelowMin: 'Số từ tối đa phải lớn hơn hoặc bằng số từ tối thiểu.',
    },
};

/**
 * Default config
 */
export const defaultEssayConfig: EssayConfig = {
    layout: {
        type: 'auto',
        splitRatio: '50-50',
        responsive: true,
        minHeight: '500px',
    },
    behavior: {
        allowRetry: true,
        showCorrectAnswer: false,
        showExplanation: false,
    },
    scoring: {
        passingThreshold: 0.7,
    },
    display: {
        showTitle: false,
        showDescription: false,
    },
    i18n: {
        defaultLanguage: 'en',
        languages: defaultEssayI18n,
    },
};

/**
 * Default data
 */
export const defaultEssayData: EssayData = {
    title: '',
    description: '',
    lessonContent: {
        title: '',
        description: '',
        sections: [],
    },
    essayQuestion: {
        question: '',
        minWords: 250,
        maxWords: 500,
        autoGrading: false,
        gradingQuestionType: 'QT_WRITE_ESSAY',
    },
};

/**
 * Schema definition
 */
export const essaySchema = {
    type: 'essay',
    title: 'Essay',
    description:
        'A content type with lesson content and essay writing questions with grading support',
    version: '1.0.0',
    category: 'assessment',
    icon: 'PenLine',
    tags: ['essay', 'writing', 'assessment', 'grading'],
    defaultData: defaultEssayData,
    defaultConfig: defaultEssayConfig,
};

/**
 * Get CSS grid columns based on split ratio
 */
export function getSplitGridCols(ratio: SplitRatio): string {
    switch (ratio) {
        case '50-50':
            return 'grid-cols-1 lg:grid-cols-2';
        case '60-40':
            return 'grid-cols-1 lg:grid-cols-[60%_40%]';
        case '40-60':
            return 'grid-cols-1 lg:grid-cols-[40%_60%]';
        case '70-30':
            return 'grid-cols-1 lg:grid-cols-[70%_30%]';
        case '30-70':
            return 'grid-cols-1 lg:grid-cols-[30%_70%]';
        default:
            return 'grid-cols-1 lg:grid-cols-2';
    }
}
